import { Container } from 'pixi.js';
import type { PickEmPrize } from '../../model/pickem/PickEmTypes';
import { PickEmBox } from './PickEmBox';

export interface PickEmGridOptions {
  boxCount: number;
  columnCount: number;
  /** Box width/height in px. Default 88. */
  boxSize?: number;
  /** Pixel spacing between boxes. Default 12. */
  spacing?: number;
  accentColor?: number;
  defaultRevealColor?: number;
  onPick: (index: number) => void;
}

/**
 * The Pick-Em grid widget — N boxes laid out in a fixed-column grid. Owns
 * each {@link PickEmBox} and forwards box taps via {@code onPick}.
 *
 * <p>Sizing: {@code totalWidth} / {@code totalHeight} return the grid's
 * natural pixel size so the host scene can scale-to-fit on narrow
 * viewports.
 */
export class PickEmGrid extends Container {
  private readonly boxes: PickEmBox[] = [];
  private readonly columnCount: number;
  private readonly boxSize: number;
  private readonly spacing: number;

  constructor(options: PickEmGridOptions) {
    super();
    this.columnCount = Math.max(1, options.columnCount);
    this.boxSize = options.boxSize ?? 88;
    this.spacing = options.spacing ?? 12;

    for (let i = 0; i < options.boxCount; i++) {
      const box = new PickEmBox({
        size: this.boxSize,
        index: i,
        accentColor: options.accentColor,
        defaultRevealColor: options.defaultRevealColor,
        onTap: options.onPick,
      });
      const row = Math.floor(i / this.columnCount);
      const col = i % this.columnCount;
      box.x = col * (this.boxSize + this.spacing) + this.boxSize / 2;
      box.y = row * (this.boxSize + this.spacing) + this.boxSize / 2;
      this.boxes.push(box);
      this.addChild(box);
    }
  }

  totalWidth(): number {
    const cols = Math.min(this.boxes.length, this.columnCount);
    return cols * this.boxSize + (cols - 1) * this.spacing;
  }

  totalHeight(): number {
    const rows = Math.ceil(this.boxes.length / this.columnCount);
    return rows * this.boxSize + (rows - 1) * this.spacing;
  }

  reveal(index: number, prize: PickEmPrize): Promise<void> {
    const box = this.boxes[index];
    if (!box) return Promise.resolve();
    return box.reveal(prize);
  }

  destroyGrid(): void {
    for (const box of this.boxes) box.destroyBox();
  }
}
